import React, { useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faCircleCheck, faLock } from "@fortawesome/free-solid-svg-icons";
import DynamicLayout from "../../../components/DynamicLayout";
import ProgressBar from "../../components/ProgressBar";
import { AcademyContext } from "../../../context/academyContext";
import { useNavigate } from "react-router-dom";

const KabbalahAcademy = () => {
  const { progress, selectedMode, updateMode, updateCurrentLesson } = useContext(AcademyContext);
  const [activeChapter, setActiveChapter] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [activePart, setActivePart] = useState(null);
  const navigate = useNavigate();

  const totalLessons = 60;

  const toggleChapter = (chapter) => {
    setActiveChapter(activeChapter === chapter ? null : chapter);
  };

  const toggleLesson = (lesson) => {
    setActiveLesson(activeLesson === lesson ? null : lesson);
  };

  const togglePart = (part) => {
    setActivePart(activePart === part ? null : part);
  };

  const renderIcon = (chapter, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activeChapter === chapter ? faChevronUp : faChevronDown}                               
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderLessonIcon = (lesson, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activeLesson === lesson ? faChevronUp : faChevronDown}
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderPartIcon = (part, isAvailable) => {
    return (
      <FontAwesomeIcon
        icon={activePart === part ? faChevronUp : faChevronDown}
        className={isAvailable ? "has-text-warning" : "has-text-grey"}
      />
    );
  };

  const renderCheckMark = (lessonNumber) => {
    return progress.completedLessons >= lessonNumber ? (
      <FontAwesomeIcon icon={faCircleCheck} className="has-text-warning ml-2" />
    ) : (
      <FontAwesomeIcon icon={faLock} className="has-text-grey ml-2" />
    );
  };

  const handleResumeClick = () => {
    let lessonNumber = progress.completedLessons + 1;
    const path = selectedMode === "beginner" ? "beginner" : "intermediate";
    updateCurrentLesson(lessonNumber);
    navigate(`/academy/kaballah/${path}/lesson`);
  };

  const handleModeChange = (mode) => {
    updateMode(mode);
    updateCurrentLesson(progress.completedLessons + 1);
  };

  const renderLesson = (lessonNumber, lessonTitle) => {
    const isCompleted = progress.completedLessons >= lessonNumber;
    return (
      <li
        className={`${isCompleted ? "is-hoverable has-text-warning" : "has-text-grey"}`}
        style={{ cursor: isCompleted ? 'pointer' : 'default' }}
        onClick={() => isCompleted && navigate(`/academy/lesson?lesson=${lessonNumber}`)}
      >
        <span className="lesson-title">
          {lessonTitle} {renderCheckMark(lessonNumber)}
        </span>
      </li>
    );
  };

  const renderCollapsibleLesson = (lessonNumber, lessonTitle, parts) => {
    const isCompleted = progress.completedLessons >= lessonNumber;
    return (
      <li
        className={`${isCompleted ? "has-text-warning" : "has-text-grey"} `}
        onClick={() => toggleLesson(lessonTitle)}
        style={{ cursor: 'pointer' }}
      >
        {lessonTitle} {renderLessonIcon(lessonTitle, isCompleted)}
        <ul className={activeLesson === lessonTitle ? "" : "is-hidden"}>
          {parts}
        </ul>
      </li>
    );
  };

  const renderPart = (partNumber, partTitle, lessons) => {
    const isCompleted = progress.completedLessons >= partNumber;
    return (
      <li
        className={`${isCompleted ? "has-text-warning" : "has-text-grey"}`}
        onClick={() => togglePart(partTitle)}
        style={{ cursor: 'pointer' }}
      >
        {partTitle} {renderPartIcon(partTitle, isCompleted)}
        <ul className={activePart === partTitle ? "" : "is-hidden"}>
          {lessons}
        </ul>
      </li>
    );
  };

  const hasStartedCourse = progress.completedLessons > 0;
  const isHardModeUnlocked = true; // Temporarily enable Intermediate mode for development

  return (
    <DynamicLayout>
       <div
        className="full-screen-image-container margin-top-0"
        style={{ backgroundImage: `url('/img/saturnwallpaper.jpg')` }}

      >
        <section className="section hasbackgroundgrey heightauto has-text-centered">
          <h2 className="title has-text-centered has-text-warning">
            {hasStartedCourse ? "Welcome back to the Kabbalah Course." : "Welcome to the Kabbalah Course."}
          </h2>
        
          <ProgressBar completedLessons={progress.completedLessons} totalLessons={totalLessons} />
          
          <ul>
            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 1 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter1")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 1: Foundational Concepts {renderIcon("chapter1", progress.completedLessons >= 1)}
              </h2>
              <ul className={activeChapter === "chapter1" ? "" : "is-hidden"}>
                {renderLesson(1, "Lesson 1: Introduction to Kabbalah")}
                {renderLesson(2, "Lesson 2: The Four Worlds")}
                {renderLesson(3, "Lesson 3: The Sefirot and Their Structure")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4  has-text-weight-bold ${progress.completedLessons >= 4 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter2")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 2: Primary Texts and Sources {renderIcon("chapter2", progress.completedLessons >= 4)}
              </h2>
              <ul className={activeChapter === "chapter2" ? "" : "is-hidden"}>
                {renderLesson(4, "Lesson 1: The Zohar")}
                {renderLesson(5, "Lesson 2: The Sefer Yetzirah")}
                {renderLesson(6, "Lesson 3: Other Kabbalistic Texts")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold  ${progress.completedLessons >= 7 ? "is-hoverable has-text-warning " : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter3")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 3: The Ten Sephiroth {renderIcon("chapter3", progress.completedLessons >= 7)}
              </h2>
              <ul className={activeChapter === "chapter3" ? "" : "is-hidden"}>
                {renderLesson(7, "Lesson 1: Overview of the Sephiroth")}
                {renderLesson(8, "Lesson 2: Kether")}
                {renderLesson(9, "Lesson 3: Chokmah")}
                {renderLesson(10, "Lesson 4: Binah")}
                {renderLesson(11, "Lesson 5: Chesed")}
                {renderLesson(12, "Lesson 6: Geburah")}
                {renderLesson(13, "Lesson 7: Tiphareth")}
                {renderLesson(14, "Lesson 8: Netzach")}
                {renderLesson(15, "Lesson 9: Hod")}
                {renderLesson(16, "Lesson 10: Yesod")}
                {renderLesson(17, "Lesson 11: Malkuth")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 18 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter4")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 4: Pathworking and the 22 Paths {renderIcon("chapter4", progress.completedLessons >= 18)}
              </h2>
              <ul className={activeChapter === "chapter4" ? "" : "is-hidden"}>
                {renderLesson(18, "Lesson 1: Introduction to Pathworking")}
                {renderLesson(19, "Lesson 2: The 22 Paths of the Tree of Life")}
                {renderLesson(20, "Lesson 3: Practical Pathworking Techniques")}
              </ul>
            </li>

            <li>
              <h2
                className={`is-size-4 has-text-weight-bold ${progress.completedLessons >= 21 ? "is-hoverable has-text-warning" : "has-text-grey"}`}
                onClick={() => toggleChapter("chapter5")}
                style={{ cursor: 'pointer' }}
              >
                Chapter 5: Angels and Archangels in Kabbalah {renderIcon("chapter5", progress.completedLessons >= 21)}
              </h2>
              <ul className={activeChapter === "chapter5" ? "" : "is-hidden"}>
                {renderLesson(21, "Lesson 1: The Role of Angels in Kabbalah")}
                {renderLesson(22, "Lesson 2: Archangels and Their Functions")}
                {renderLesson(23, "Lesson 3: Invoking Angels in Kabbalistic Practices")}
              </ul>
            </li>
          </ul>

          <button className="button is-warning mt-6" onClick={handleResumeClick}>
            {hasStartedCourse ? "Resume" : "Start"}
          </button>

          <div className="mt-6 buttons has-addons is-centered">
            <button
              className={`button is-small ${selectedMode === "beginner" ? "is-warning" : ""}`}
              onClick={() => handleModeChange("beginner")}
            >
              Beginner
            </button>
            <button
              className={`button is-small ${selectedMode === "intermediate" ? "is-warning" : ""} ${isHardModeUnlocked ? '' : 'is-locked'}`}
              onClick={() => handleModeChange("intermediate")}
              disabled={!isHardModeUnlocked}
              title={isHardModeUnlocked ? '' : 'Complete all lessons to unlock Intermediate mode'}
            >
              {!isHardModeUnlocked && <FontAwesomeIcon icon={faLock} className="lock-icon" />}
              Intermediate
            </button>
          </div>
        </section>
      </div>
    </DynamicLayout>
  );
};

export default KabbalahAcademy;
